/**
 * @fileoverview Buildings Table Configuration
 *
 * Centralized configuration for buildings list table.
 * Defines columns, sort logic, filters, and export configurations.
 *
 * @module Buildings/TableConfig
 */

import {
  TableColumnsConfig,
  TableSortConfig,
  FilterSection,
  GroupByOption,
  UniversalFilter,
  ColumnConfig
} from 'shared/components/UniversalTable/types';

// ========== BUILDINGS COLUMN CONFIGURATION ==========

export const BUILDINGS_COLUMNS: ColumnConfig[] = [
  {
    id: 'checkbox',
    label: '',
    minWidth: 50,
    weight: 0,
    align: 'center',
    className: 'flex justify-center items-center'
  },
  {
    id: 'id',
    label: 'ID',
    minWidth: 110,
    weight: 0,
    sortKey: 'id'
  },
  {
    id: 'address',
    label: 'Adres',
    minWidth: 300,
    weight: 5,
    sortKey: 'googlePlaceLongAddress'
  },
  {
    id: 'company',
    label: 'Bedrijf',
    minWidth: 180,
    weight: 3,
    sortKey: 'company'
  },
  {
    id: 'units',
    label: 'Units',
    minWidth: 90,
    weight: 0,
    sortKey: 'units',
    align: 'center'
  },
  {
    id: 'tickets',
    label: 'Tickets',
    minWidth: 100,
    weight: 0,
    align: 'center'
  },
  {
    id: 'dueDiligence',
    label: 'Due Diligence',
    minWidth: 250,
    weight: 4
  },
  {
    id: 'cashOut',
    label: 'Cash Out',
    minWidth: 160,
    weight: 2,
    sortKey: 'totalCashOutIncludingRenovation'
  },
  {
    id: 'rent',
    label: 'Bruto Huur/Jaar',
    minWidth: 180,
    weight: 2,
    sortKey: 'grossActualRentalIncomePerYear'
  },
  {
    id: 'mobiscore',
    label: 'Mobiscore',
    minWidth: 110,
    weight: 0,
    sortKey: 'mobiscore',
    align: 'center'
  },
  {
    id: 'breeam',
    label: 'BREEAM',
    minWidth: 120,
    weight: 0,
    sortKey: 'breeamRating',
    align: 'center'
  },
  {
    id: 'well',
    label: 'WELL',
    minWidth: 100,
    weight: 0,
    sortKey: 'wellRating',
    align: 'center'
  },
  {
    id: 'actions',
    label: 'Acties',
    minWidth: 130,
    weight: 0,
    align: 'center',
    className: 'flex justify-center items-center'
  }
];

// Legacy config for backward compatibility
export const BUILDINGS_COLUMNS_CONFIG: TableColumnsConfig = {
  columns: BUILDINGS_COLUMNS.map(col => ({
    ...col,
    defaultWeight: col.weight
  })) as any,
  localStorageKey: 'buildings-table-column-widths'
};

// ========== BUILDINGS SORT CONFIGURATION ==========

export const BUILDINGS_SORT_CONFIG: TableSortConfig = {
  defaultSortKey: 'id',
  defaultDirection: 'desc',

  fields: [
    {
      key: 'id',
      extractor: (row) => row.id || ''
    },
    {
      key: 'googlePlaceLongAddress',
      extractor: (row) => row.googlePlaceLongAddress?.toLowerCase() || ''
    },
    {
      key: 'company',
      extractor: (row) => row.company?.name?.toLowerCase() || ''
    },
    {
      key: 'units',
      extractor: (row) => typeof row.units === 'number' ? row.units : row.units?.length || 0
    },
    {
      key: 'totalCashOutIncludingRenovation',
      extractor: (row) => row.totalCashOutIncludingRenovation || 0
    },
    {
      key: 'grossActualRentalIncomePerYear',
      extractor: (row) => row.grossActualRentalIncomePerYear || 0
    },
    {
      key: 'mobiscore',
      extractor: (row) => row.mobiscore || 0
    },
    {
      key: 'breeamRating',
      extractor: (row) => row.breeamRating || ''
    },
    {
      key: 'wellRating',
      extractor: (row) => row.wellRating || ''
    }
  ],

  caseSensitive: false
};

// ========== BUILDINGS GROUPING OPTIONS ==========

export const BUILDINGS_GROUPBY_OPTIONS: GroupByOption[] = [
  { key: 'none', label: 'Geen' },
  { key: 'company', label: 'Per bedrijf' },
  { key: 'buildingType', label: 'Per gebouwtype' },
];

// ========== BUILDINGS FILTER SECTIONS ==========

export const BUILDINGS_FILTER_SECTIONS: FilterSection[] = [
  {
    key: 'buildingType',
    label: 'Gebouwtype',
    options: [], // Will be populated dynamically from data
    color: 'moss',
    multiSelect: true
  },
  {
    key: 'company',
    label: 'Bedrijf',
    options: [], // Will be populated dynamically from data
    color: 'brick',
    multiSelect: true
  },
];

// ========== BUILDINGS UNIVERSAL FILTERS ==========

export const BUILDINGS_UNIVERSAL_FILTERS: UniversalFilter[] = [
  { type: 'search', label: 'Zoeken', enabled: true },
];

// ========== COMBINED CONFIGURATION ==========

// Configuration object for spreading into UniversalTable props
export const BUILDINGS_TABLE_CONFIG = {
  columnsConfig: BUILDINGS_COLUMNS_CONFIG,
  sortConfig: BUILDINGS_SORT_CONFIG,
  storageKey: 'buildings-table-column-widths'
};
